import { useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  setIsRevieweesOverlappingEmployees,
  useIsRevieweesCustomViewsSettings,
  useReviewees,
} from 'reducers/reviewCreationSlice';
import { setShowViewsPanel } from 'reducers/viewsSlice';

import { NeedAttention, Settings } from 'assets/assetsV1/iconComponents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TagOutlinedList } from 'uiToolkitV1/TagOutlinedList';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  useGetOverlappingViewEmployees,
  useInvalidateOverlappingViewEmployees,
  useInvalidateViewEmployees,
} from '../../Hooks/dataHooks/revieweesDataHooks';

const RevieweesOverlappingEmployees = () => {
  const reviewees = useReviewees();
  const dispatch = useDispatch();
  const isRevieweesCustomViewsSettings = useIsRevieweesCustomViewsSettings();

  const {
    data: employeeList,
    isLoading,
    isSuccess,
    isRefetching,
  } = useGetOverlappingViewEmployees({ views: reviewees, isRevieweesCustomViewsSettings });

  const overlappingEmpDataInvalidator = useInvalidateOverlappingViewEmployees();
  const viewsEmployeesDataInvalidator = useInvalidateViewEmployees();

  useEffect(() => {
    if (!isRevieweesCustomViewsSettings) {
      return;
    }

    if (employeeList?.length === 0) {
      dispatch(setIsRevieweesOverlappingEmployees(false));
    } else if (employeeList?.length > 0) {
      dispatch(setIsRevieweesOverlappingEmployees(true));
    }
  }, [dispatch, employeeList, isRevieweesCustomViewsSettings]);

  const clickManageViewsHandler = () => {
    dispatch(
      setShowViewsPanel({
        onSuccess: () => {
          overlappingEmpDataInvalidator();
          viewsEmployeesDataInvalidator();
        },
        onCancel: () => {
          overlappingEmpDataInvalidator();
          viewsEmployeesDataInvalidator();
        },
        // constraints: {
        //   disallowPrivate: true,
        //   allowOnlyMyCompany: true
        // }
        constraints: {
          disallowPrivate: true,
          allowOnlyMyCompany: true,
          source: 'REVIEW',
          disableApply: true,
        },
        managePanelConstraints: {
          showMyCompanyViewsOnly: true,
          // showSelfViewsOnly: true
        },
      })
    );
  };

  if (!isRevieweesCustomViewsSettings || reviewees.length < 2 || employeeList?.length === 0) {
    return null;
  }

  if (isLoading || isRefetching || !isSuccess) {
    return (
      <div style={{ marginTop: '16px' }}>
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <Box p="0 8px 10px 8px" mt={3}>
      <Flex border="1px solid" borderColor="border.EBEFF3" borderRadius="4px" flexDir="column">
        {/* header */}
        <Flex justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" p="20px">
            <NeedAttention boxSize="24px" color="alerts.negative" mr={2} />
            <TextBodyMedium color="alerts.negative" bold>
              {employeeList.length} employee{employeeList.length > 1 ? 's' : ''}{' '}
              {employeeList.length > 1 ? 'are' : 'is'} repeating in multiple views, please edit from rest of the views
            </TextBodyMedium>
          </Flex>
          <Button variant="tertiary" mr={3} StartIcon={Settings} onClick={clickManageViewsHandler}>
            Manage Views
          </Button>
        </Flex>

        <Divider />

        {/* emoloyee list */}
        <Box overflowY="auto" px={2} maxH="375px">
          {employeeList.map(({ employee, views }, index) => {
            const { displayName, profilePhoto, email } = employee;
            const tagListData = views.map((view) => ({
              id: view.id,
              title: view.name,
            }));

            return (
              <Flex
                alignItems="center"
                py="2"
                borderBottom={index !== employeeList.length - 1 ? '1px solid' : 'none'}
                borderColor="border.EBEFF3"
                key={index}
              >
                <Box p="10px 12px" minW="320px">
                  <ListItemComp
                    title={displayName}
                    subTitle={email}
                    LeftComponent={() => <Avatar src={profilePhoto} />}
                    withoutBackground
                    isBoldText={false}
                  />
                </Box>
                <TagOutlinedList tagList={tagListData} />
              </Flex>
            );
          })}
        </Box>
      </Flex>
    </Box>
  );
};

export { RevieweesOverlappingEmployees };
